import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Mail, Send, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Card, CardContent } from '../components/ui/card';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator, BreadcrumbPage } from '../components/ui/breadcrumb';
import SEO from '../components/SEO';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const locations = [
  { value: '44', label: 'Loire-Atlantique (44)' },
  { value: '85', label: 'Vendée (85)' },
  { value: '56', label: 'Morbihan (56)' },
  { value: '49', label: 'Maine-et-Loire (49)' },
];

const projectTypes = [
  { value: 'achat-spa', label: 'Achat d\'un spa' },
  { value: 'achat-spa-nage', label: 'Achat d\'un spa de nage' },
  { value: 'achat-bain-froid', label: 'Achat d\'un bain froid' },
  { value: 'piece-detachee', label: 'Pièce détachée' },
  { value: 'accessoire', label: 'Accessoire' },
  { value: 'autre', label: 'Autre demande' },
];

const budgets = [
  { value: 'moins-5000', label: 'Moins de 5 000 € TTC' },
  { value: '5000-8000', label: '5 000 € - 8 000 € TTC' },
  { value: '8000-12000', label: '8 000 € - 12 000 € TTC' },
  { value: 'plus-12000', label: 'Plus de 12 000 € TTC' },
  { value: 'non-defini', label: 'Non défini' },
];

const DevisPage = () => {
  const [searchParams] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [spas, setSpas] = useState([]);
  
  const preselectedSpa = searchParams.get('spa') || '';
  const preselectedPiece = searchParams.get('piece') || '';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    projectType: preselectedPiece ? 'piece-detachee' : 'achat-spa',
    budget: '',
    spaModel: preselectedSpa,
    message: preselectedPiece ? `Demande pour: ${preselectedPiece}` : '',
    contactPreference: 'phone',
    // Options d'intérêt (pour les spas)
    optionPompeAChaleur: false,
    optionEscalier: false,
    optionLeveCouverture: false,
  });

  useEffect(() => {
    const fetchSpas = async () => {
      try {
        const response = await axios.get(`${API}/spas`);
        setSpas(response.data);
      } catch (error) {
        console.error('Error fetching spas:', error);
      }
    };
    fetchSpas();
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Build options string if any options are selected
    const selectedOptions = [];
    if (formData.optionPompeAChaleur) selectedOptions.push('Pompe à chaleur');
    if (formData.optionEscalier) selectedOptions.push('Escalier');
    if (formData.optionLeveCouverture) selectedOptions.push('Lève-couverture');
    
    const optionsText = selectedOptions.length > 0 
      ? `\n\nOptions intéressées: ${selectedOptions.join(', ')}`
      : '';

    try {
      await axios.post(`${API}/leads`, {
        type: 'devis',
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        location: formData.location,
        project_type: formData.projectType,
        budget: formData.budget,
        product_name: formData.spaModel,
        message: formData.message + optionsText,
      });

      // Track devis form submission
      trackDevisFormSubmit({
        product_slug: preselectedSpa || preselectedPiece || '',
        product_name: formData.spaModel || ''
      });
      setSubmitted(true);
      toast.success('Demande envoyée !');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="devis-success">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Demande envoyée !</h1>
          <p className="text-slate-600 mb-8">
            Merci {formData.name}. Notre technicien vous recontactera sous 24h pour étudier votre projet.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/spas">
              <Button variant="outline" className="gap-2">
                Voir nos spas
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/">
              <Button className="bg-slate-900 hover:bg-slate-800">
                Retour à l'accueil
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="devis-page">
      <SEO 
        title="Devis Gratuit Spa - Installation sur mesure"
        description="Demandez votre devis gratuit pour l'achat et l'installation d'un spa à Nantes et Grand Ouest. Réponse sous 24h par notre technicien spécialiste."
        canonical="/devis"
      />
      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Accueil</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Demande de Devis</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Demandez votre devis gratuit
          </h1>
          <p className="text-lg text-slate-600">
            Notre technicien vous recontacte sous 24h pour étudier votre projet
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <Card className="border-slate-200">
              <CardContent className="p-6 md:p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Contact Info */}
                  <div>
                    <h3 className="font-semibold text-lg text-slate-900 mb-4">Vos coordonnées</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Nom complet *</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => handleChange('name', e.target.value)}
                          required
                          placeholder="Jean Dupont"
                          data-testid="input-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Téléphone *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleChange('phone', e.target.value)}
                          required
                          placeholder="06 12 34 56 78"
                          data-testid="input-phone"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          required
                          placeholder="jean.dupont@email.com"
                          data-testid="input-email"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Project Info */}
                  <div>
                    <h3 className="font-semibold text-lg text-slate-900 mb-4">Votre projet</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Département *</Label>
                        <Select value={formData.location} onValueChange={(v) => handleChange('location', v)} required>
                          <SelectTrigger data-testid="select-location">
                            <SelectValue placeholder="Sélectionnez" />
                          </SelectTrigger>
                          <SelectContent>
                            {locations.map(loc => (
                              <SelectItem key={loc.value} value={loc.value}>{loc.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Type de projet *</Label>
                        <Select value={formData.projectType} onValueChange={(v) => handleChange('projectType', v)} required>
                          <SelectTrigger data-testid="select-project">
                            <SelectValue placeholder="Sélectionnez" />
                          </SelectTrigger>
                          <SelectContent>
                            {projectTypes.map(pt => (
                              <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {formData.projectType.includes('achat') && (
                        <>
                          <div>
                            <Label>Budget envisagé</Label>
                            <Select value={formData.budget} onValueChange={(v) => handleChange('budget', v)}>
                              <SelectTrigger data-testid="select-budget">
                                <SelectValue placeholder="Sélectionnez" />
                              </SelectTrigger>
                              <SelectContent>
                                {budgets.map(b => (
                                  <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Modèle souhaité (optionnel)</Label>
                            <Select value={formData.spaModel} onValueChange={(v) => handleChange('spaModel', v)}>
                              <SelectTrigger data-testid="select-spa">
                                <SelectValue placeholder="Choisir un modèle" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Je ne sais pas encore</SelectItem>
                                {spas.map(spa => (
                                  <SelectItem key={spa.slug} value={spa.slug}>{spa.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {/* Options qui vous intéressent */}
                          <div className="col-span-2">
                            <Label className="mb-3 block text-slate-700">Options qui vous intéressent (optionnel)</Label>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                              <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-slate-300 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  checked={formData.optionPompeAChaleur}
                                  onChange={(e) => handleChange('optionPompeAChaleur', e.target.checked)}
                                  className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                                />
                                <span className="text-sm text-slate-700">Pompe à chaleur</span>
                              </label>
                              <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-slate-300 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  checked={formData.optionEscalier}
                                  onChange={(e) => handleChange('optionEscalier', e.target.checked)}
                                  className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                                />
                                <span className="text-sm text-slate-700">Escalier</span>
                              </label>
                              <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-slate-300 cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  checked={formData.optionLeveCouverture}
                                  onChange={(e) => handleChange('optionLeveCouverture', e.target.checked)}
                                  className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                                />
                                <span className="text-sm text-slate-700">Lève-couverture</span>
                              </label>
                            </div>
                            <p className="text-xs text-slate-500 mt-2">Ces choix servent uniquement à orienter notre échange.</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <Label htmlFor="message">Votre message (optionnel)</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      placeholder="Décrivez votre projet, vos questions, contraintes particulières..."
                      rows={4}
                      data-testid="input-message"
                    />
                  </div>

                  {/* Contact Preference */}
                  <div>
                    <Label className="mb-3 block">Comment préférez-vous être contacté ?</Label>
                    <RadioGroup
                      value={formData.contactPreference}
                      onValueChange={(v) => handleChange('contactPreference', v)}
                      className="flex gap-6"
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="phone" id="pref-phone" />
                        <Label htmlFor="pref-phone" className="font-normal cursor-pointer">Téléphone</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="email" id="pref-email" />
                        <Label htmlFor="pref-email" className="font-normal cursor-pointer">Email</Label>
                      </div>
                      <div className="flex items-center gap-2">
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Submit */}
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-orange-500 hover:bg-orange-600 btn-lift"
                    disabled={loading}
                    data-testid="submit-devis"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Envoi en cours...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="w-5 h-5" />
                        Envoyer ma demande
                      </span>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="border-slate-200 bg-slate-900 text-white">
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg mb-4">Besoin d'aide ?</h3>
                <p className="text-slate-300 text-sm mb-4">
                  Notre équipe est disponible pour répondre à vos questions.
                </p>
                <div className="space-y-3">
                  <a href="mailto:contact@atlantiquespaboutique.com" className="flex items-center gap-3 text-white hover:text-orange-400 transition-colors">
                    <Mail className="w-5 h-5" />
                    <span>contact@atlantiquespaboutique.com</span>
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg text-slate-900 mb-4">Notre accompagnement</h3>
                <ul className="space-y-3 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    Étude personnalisée de votre projet
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    Vérification de l'installation électrique
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    Installation par technicien expert
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    Mise en service professionnelle
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    Formation à l'utilisation
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    SAV local garanti
                  </li>
                </ul>
                <p className="text-xs text-slate-500 mt-4 pt-4 border-t border-slate-100">
                  Livraison et installation sur devis selon votre configuration.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevisPage;
